// Клиент эндпоинтов лайфхаков.
//
// Список читается без входа, поэтому GET ходит мимо `request()` — тот при 401
// дёргает общий обработчик «нужен вход», а страницу открывают гости, и пустой
// список у них не ошибка. Всё, что меняет данные, наоборот идёт через
// `request()`: там уже нужен вход, и коды ошибок должны разбираться как везде.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';
import type { Category } from './catalog';

export type LifehackStatus = 'pending' | 'published' | 'rejected';

/** Кто написал совет: человек с витрины или мы сами. */
export type LifehackSource = 'user' | 'editorial';

export interface Lifehack {
  id: string;
  text: string;
  /** Язык, на котором приехал `text`: перевода на язык интерфейса могло и не
   *  быть, тогда сервер отдаёт ближайший доступный. */
  lang: string;
  /** Язык оригинала. Расходится с `lang` ровно тогда, когда перевода нет. */
  sourceLang: string;
  source: LifehackSource;
  /** ISO 3166-1 alpha-2 или пустая строка — «страна не выбрана». */
  country: string;
  category: Category | '';
  status: LifehackStatus;
  createdAt: number;
  authorId: string;
  /** Имя автора; пустое — человек его не заполнил, подпись подставит интерфейс. */
  authorName: string;
  confirms: number;
  /** Подтверждал ли этот читатель. У гостя всегда false. */
  confirmed: boolean;
}

export interface Facet {
  value: string;
  count: number;
}

export interface LifehackList {
  items: Lifehack[];
  facets: { countries: Facet[]; categories: Facet[] };
}

export interface AdminLifehackList {
  items: Lifehack[];
  counts: Record<LifehackStatus, number>;
}

export interface NewLifehack {
  text: string;
  country?: string;
  category?: string;
  /** Язык интерфейса автора: на нём написан текст, и переводить потом будут с него. */
  lang?: string;
}

const EMPTY: LifehackList = { items: [], facets: { countries: [], categories: [] } };

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/**
 * Опубликованные лайфхаки. Сеть недоступна или ответ битый — пустой список:
 * страница обязана нарисоваться, а «ничего не нашлось» и «сервер не ответил»
 * для читателя витрины выглядят одинаково и одинаково не требуют модалки.
 */
export async function fetchLifehacks(params: {
  country?: string;
  category?: string;
  mine?: boolean;
  /** Язык читателя: тексты приезжают уже на нём, а не переводятся на клиенте. */
  lang?: string;
}): Promise<LifehackList> {
  try {
    const res = await fetch(
      `/api/lifehacks${query({
        country: params.country,
        category: params.category,
        mine: params.mine ? '1' : undefined,
        lang: params.lang,
      })}`,
      { headers: { accept: 'application/json' } },
    );
    if (!res.ok) return EMPTY;
    const body = (await res.json()) as Partial<LifehackList> | null;
    return {
      items: Array.isArray(body?.items) ? body.items : [],
      facets: {
        countries: body?.facets?.countries ?? [],
        categories: body?.facets?.categories ?? [],
      },
    };
  } catch {
    return EMPTY;
  }
}

export function createLifehack(input: NewLifehack): Promise<ApiResult<{ item: Lifehack }>> {
  return request('/api/lifehacks', { method: 'POST', json: input });
}

/**
 * Подтверждение — переключатель: PUT ставит, DELETE снимает.
 *
 * `lang` едет и сюда: в ответе приходит обновлённая строка, которой страница
 * заменяет карточку на месте, — без языка читателя текст в ней подменился бы
 * английским прямо под пальцем.
 */
export function setConfirm(
  id: string,
  on: boolean,
  lang?: string,
): Promise<ApiResult<{ item: Lifehack }>> {
  return request(`/api/lifehacks/${encodeURIComponent(id)}/confirm${query({ lang })}`, {
    method: on ? 'PUT' : 'DELETE',
  });
}

export function deleteLifehack(id: string): Promise<ApiResult<{ ok: true }>> {
  return request(`/api/lifehacks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Админка ─────────────────────────────────────────────────────────────────

export function fetchAdminLifehacks(
  status: LifehackStatus,
  lang?: string,
): Promise<ApiResult<AdminLifehackList>> {
  return request(`/api/admin/lifehacks${query({ status, lang })}`);
}

export function setAdminStatus(
  id: string,
  status: LifehackStatus,
  lang?: string,
): Promise<ApiResult<{ item: Lifehack }>> {
  return request(`/api/admin/lifehacks/${encodeURIComponent(id)}${query({ lang })}`, {
    method: 'PATCH',
    json: { status },
  });
}

export function deleteAdminLifehack(id: string): Promise<ApiResult<{ ok: true }>> {
  return request(`/api/admin/lifehacks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
